import { Observable ,  combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { SortEventData } from '../../components/pay-info/pay-info-table/ignite/web-components/sort-event';
import { PaginatorPageEventRange } from '../../components/pay-info/pay-info-table/ignite/web-components/paginator-page-event';

export class GridDataSource<T> {
  customSort: (data: T[], sortEvent: SortEventData) => T[] = null;
  processPage: (pageData: T[]) => void = null;

  constructor(
    private _data: Observable<T[]>,
    private _page: Observable<PaginatorPageEventRange>,
    private _sort: Observable<SortEventData>
  ) {
  }

  static isEmptySort(sortEvent: SortEventData) {
    return !sortEvent.key || !sortEvent.key.length || sortEvent.isAsc === 'none';
  }

  connect(): Observable<T[]> {
    const sortedDataSource = combineLatest([this._data, this._sort]).pipe(
      map(([data, sortEvent]: [T[], SortEventData]) => this._sortData(data, sortEvent)));

    return combineLatest([sortedDataSource, this._page]).pipe(
      map(([sortedData, pageEvent]: [T[], PaginatorPageEventRange]) => {
        const startIndex = pageEvent.start;
        const page = sortedData.slice(startIndex, pageEvent.end + 1);
        if (this.processPage) {
          this.processPage(page);
        }

        return page;
      }));
  }

  defaultSort(data: T[], sort: SortEventData | SortEventData[]): T[] {
    if (!data) {
      return data;
    }
    if (!data.length) {
      return data.slice();
    }

    const sorts = this._getSorts(sort);
    if (!sorts) {
      return data.slice();
    }

    const propertyPaths = sorts.map(s => s.key.split('.'));
    const getSortValues = propertyPaths.map(propertyPath => item => {
      let current = item;
      let i = 0;
      while (i < propertyPath.length && current != null) {
        current = current[propertyPath[i]];
        i++;
      }

      return current;
    });

    return data.slice().sort((a, b) => {
      for (let i = 0; i < getSortValues.length; i++) {
        const aValue = getSortValues[i](a);
        const bValue = getSortValues[i](b);

        let compared: number;
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          compared = aValue.localeCompare(bValue);
        } else {
          compared = aValue - bValue;
        }

        if (compared !== 0 || i === getSortValues.length - 1) {
          return sorts[i].isAsc
            ? compared
            : -compared;
        }
      }
    });
  }

  private _getSorts(sort: SortEventData | SortEventData[]): SortEventData[] {
    let sorts: SortEventData[];
    if (!Array.isArray(sort)) {
      sorts = [sort];
    } else {
      sorts = sort;
    }

    for (let i = sorts.length - 1; i >= 0; i--) {
      if (GridDataSource.isEmptySort(sorts[i])) {
        sorts.splice(i, 1);
      }
    }
    if (!sorts.length) {
      return null;
    }

    return sorts;
  }

  private _sortData(data: T[], sortEvent: SortEventData): T[] {
    if (GridDataSource.isEmptySort(sortEvent)) {
      return data;
    }

    if (this.customSort) {
      return this.customSort(data, sortEvent);
    }

    return this.defaultSort(data, sortEvent);
  }
}
